import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import Modal from './Modal';
import { Header } from './Header';
import Loader from './Loader';
import { Suspense } from 'react';
import Action from './Action';
import useCopy from '../util/useCopy';
import OneLeagueResource from '../resources/League/One';
import AllUserProfilesResource from '../resources/AllUserProfiles';
import LeagueMembersInfoResource from '../resources/League/MembersInfo';
import avatars from '../config/avatars';
import { kickFromLeagueMutation } from '../resources/League/mutations';
import UserIcon from './UserIcon';
import UserXIcon from './UserXIcon';
import SearchIcon from './SearchIcon';
import CloseIcon from './CloseIcon';
import { getPhoneLocation } from '../util/getPhoneLocation';
import { makeClassName } from '@siteless/util';

let ManageMembersModalContent = ({
  onClose,
  leagueId,
}: {
  onClose: () => void;
  leagueId: string;
}) => {
  let copy = useCopy();
  let league = OneLeagueResource.use(leagueId);
  let enums = AllUserProfilesResource.use();
  let membersInfo = LeagueMembersInfoResource.use(leagueId);

  let [searchQuery, setSearchQuery] = useState('');
  let [showSearch, setShowSearch] = useState(false);
  let [filterJoinedThisYear, setFilterJoinedThisYear] = useState(false);


  if (!league) return null;

  let showFilters = league.memberUserIds.length > 10;
  let thisYear = new Date().getFullYear();

  let filteredMemberUserIds = useMemo(() => {
    let ids = [...league.memberUserIds].sort((a, b) => {
      if (a === league.ownerUserId) return -1;
      if (b === league.ownerUserId) return 1;

      let aHandle = enums.users[a]?.handle;
      let bHandle = enums.users[b]?.handle;
      if (aHandle && !bHandle) return -1;
      if (bHandle && !aHandle) return 1;

      let aCreated = membersInfo[a]?.createdAt;
      let bCreated = membersInfo[b]?.createdAt;
      if (aCreated && bCreated) return new Date(bCreated).getTime() - new Date(aCreated).getTime();

      return 0;
    });

    if (filterJoinedThisYear) {
      ids = ids.filter((userId) => {
        let createdAt = membersInfo[userId]?.createdAt;
        if (!createdAt) return false;
        return new Date(createdAt).getFullYear() === thisYear;
      });
    }

    if (searchQuery) {
      let q = searchQuery.toLowerCase();
      ids = ids.filter((userId) => {
        let user = enums.users[userId];
        let info = membersInfo[userId];
        let handle = user?.handle?.toLowerCase() || '';
        let email = info?.email?.toLowerCase() || '';
        let phone = info?.phoneNumber || '';
        return handle.includes(q) || email.includes(q) || phone.includes(q);
      });
    }

    return ids;
  }, [league.memberUserIds, league.ownerUserId, enums, membersInfo, filterJoinedThisYear, searchQuery, thisYear]);

  return (
      <div className="relative flex h-full flex-col">
        <div className="flex-none bg-white">
          <Header className="mx-4 border-b-4 border-neutral-100 px-2 py-4">
            {copy.Members}
          </Header>
        </div>
        {showFilters && (
          <div className="flex flex-none items-center gap-2 px-4 pt-3 pb-1">
            <button
              className={makeClassName(
                'flex-none cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium',
                filterJoinedThisYear
                  ? 'bg-rose-500 text-white'
                  : 'bg-neutral-100 text-neutral-500',
              )}
              onClick={() => setFilterJoinedThisYear(!filterJoinedThisYear)}
            >
              {copy.joinedIn} {thisYear}
            </button>
            <motion.div
              animate={{ width: showSearch ? '60%' : 36 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="cursor-pointer overflow-hidden rounded-full bg-neutral-100"
              onClick={() => !showSearch && setShowSearch(true)}
            >
              {showSearch ? (
                <div className="flex items-center gap-2 px-3 py-1.5">
                  <SearchIcon className="h-4 w-4 flex-none text-neutral-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={copy['search...']}
                    className="min-w-0 flex-1 bg-transparent text-sm text-purple-900 outline-none placeholder:text-neutral-400"
                    autoFocus
                  />
                  <button
                    className="flex-none cursor-pointer text-neutral-400"
                    onClick={() => {
                      setShowSearch(false);
                      setSearchQuery('');
                    }}
                  >
                    <CloseIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center p-2 text-neutral-500">
                  <SearchIcon className="h-4 w-4" />
                </div>
              )}
            </motion.div>
          </div>
        )}
        <div className="relative min-h-0 flex-1 overflow-auto px-4">
          <div className="sticky top-0 z-10 h-4 bg-gradient-to-b from-white to-transparent" />
          <div className="space-y-2 pb-4">
            {filteredMemberUserIds.map((userId) => {
              let user = enums.users[userId];
              let memberInfo = membersInfo[userId];
              let isOwner = userId === league.ownerUserId;
              let hasHandle = Boolean(user?.handle);
              let hasAvatar = Boolean(user?.avatarId);
              let AvatarComponent = hasAvatar
                ? avatars[user.avatarId || 'AVATAR_0'].ArtworkComponent
                : null;

              let phoneNumber = memberInfo?.phoneNumber;
              let phoneLocation = phoneNumber ? getPhoneLocation(phoneNumber) : undefined;
              let phoneDisplay = phoneNumber
                ? phoneLocation ? `${phoneNumber} (${phoneLocation})` : phoneNumber
                : undefined;
              let contactInfo = memberInfo?.email || phoneDisplay;
              let confirmName = user?.handle || memberInfo?.email || phoneNumber || 'this user';

              return (
                <div
                  key={userId}
                  className="flex items-center justify-between rounded-lg bg-neutral-50 px-3 py-2"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-200">
                      {AvatarComponent ? (
                        <AvatarComponent
                          className="h-full w-full"
                          backgroundColor={user?.avatarBackgroundHex || undefined}
                        />
                      ) : (
                        <UserIcon className="h-6 w-6 text-neutral-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      {hasHandle ? (
                        <div className="truncate font-medium text-purple-900">
                          {user.handle}
                        </div>
                      ) : (
                        <div className="italic text-neutral-400">
                          {copy.newMember}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-neutral-500">
                        {isOwner && <span>{copy.Owner}</span>}
                        {contactInfo && (
                          <span className="truncate">{contactInfo}</span>
                        )}
                      </div>
                      {memberInfo?.createdAt && (
                        <div className="text-xs text-neutral-400">
                          {copy.joined} {new Date(memberInfo.createdAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                  {!isOwner && (
                    <Action
                      disablePadding
                      Icon={UserXIcon}
                      className="flex-shrink-0 text-neutral-400"
                      onClick={() => {
                        if (
                          !window.confirm(
                            `${copy.areYouSureYouWantToKick} ${confirmName}?`,
                          )
                        )
                          return;
                        kickFromLeagueMutation({
                          leagueId,
                          kickedUserId: userId,
                          ownerUserId: league.ownerUserId,
                        });
                      }}
                    />
                  )}
                </div>
              );
            })}
            {showFilters && filteredMemberUserIds.length === 0 && (
              <div className="py-4 text-center text-sm text-neutral-400">
                {copy.noMembersMatch}
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

let ManageMembersModal = ({
  isOpen,
  onClose,
  leagueId,
}: {
  isOpen: boolean;
  onClose: () => void;
  leagueId: string;
}) => (
  <Modal isOpen={isOpen} onClose={onClose} disableBodyPadding className="flex">
    <Suspense fallback={<Loader />}>
      <ManageMembersModalContent onClose={onClose} leagueId={leagueId} />
    </Suspense>
  </Modal>
);

export default ManageMembersModal;
